/**
 * Zod create/update schemas for every CRUD resource, plus the inferred input
 * types. Mirrors IMPLEMENTATION_CONTRACT §4.3.
 *
 * Conventions:
 * - Create schemas exclude server-managed fields (`id`, `created_at`,
 *   `updated_at`) and computed/trace fields.
 * - Columns with a DB default (status, version, priority, …) are `.optional()`
 *   so callers may omit them; the repository / route fills the default.
 * - Update schemas are `.partial()` of the create schema unless noted.
 * - Enums use `z.enum(<X_VALUES>)` with the shared tuples.
 */

import { z } from 'zod';

import {
  ACCESS_MODES,
  ACTION_CARD_CATEGORIES,
  ACTION_CARD_GENERATED_BY_TYPES,
  ACTION_CARD_LINK_TYPES,
  ACTION_CARD_LINK_VISIBILITY,
  ACTION_CARD_SEVERITIES,
  ACTION_CARD_STATUSES,
  ACTION_CARD_SUBJECT_TYPES,
  ACTION_CARD_SUGGESTED_ACTION_TYPES,
  ACTION_CARD_TYPES,
  ACTION_POLICY_RISK_LEVELS,
  AGENT_ACTIVATION_KINDS,
  AGENT_ACTIVATION_SOURCES,
  AGENT_BINDING_STATUS,
  AGENT_HARNESS_RUN_STATUS,
  AGENT_PROFILE_KINDS,
  AGENT_PROFILE_SPECIALIZATIONS,
  AGENT_PROFILE_STATUS,
  AGENT_PROFILE_VERSION_STATUS,
  ACTOR_MEMBERSHIP_TYPES,
  ACTOR_STATUS,
  ACTOR_TYPES,
  APPROVAL_DECISIONS,
  APPROVAL_MODES,
  APPROVAL_REQUEST_STATUS,
  AUTHORITY_STATUSES,
  ARTIFACT_STATUS,
  ARTIFACT_TYPES,
  BINDING_ENTITY_TYPES,
  BINDING_TYPES,
  CLAIM_STATUS,
  CLAIM_TYPES,
  CONFIDENTIALITY,
  CONTEXT_MATURITY_STAGES,
  CONTEXT_ACTION_MODES,
  CONTEXT_CRYSTALLIZATION_TARGET_TYPES,
  CONTEXT_MONITORING_CADENCES,
  CONTEXT_MONITORING_PROFILE_KINDS,
  CONTEXT_MONITORING_STATUS,
  CONTEXT_POLICIES,
  CONTEXT_TYPES,
  LENS_MATURITY,
  LENS_VALIDATION_STATUS,
  CREDENTIAL_GRANT_STATUS,
  CREDENTIAL_STATUS,
  CRYSTALLIZATION_STAGES,
  DECISION_STATUS,
  EFFECT_TYPES,
  ENTITY_STATUS,
  EVENT_TYPES,
  EVIDENCE_POLARITY,
  EXECUTOR_TYPES,
  IDENTITY_STATUS,
  LINK_TYPES,
  PACKAGE_DEPLOYMENT_STATUSES,
  PACKAGE_DEPLOYMENT_UPGRADE_MODES,
  PACKAGE_IMPROVEMENT_RISK_LEVELS,
  PACKAGE_MATURITY_STAGES,
  PACKAGE_TYPES,
  PACKAGE_USAGE_PRIVACY_VISIBILITY,
  PACKAGE_VERSION_STATUSES,
  PRIORITY,
  ROLE_ASSIGNMENT_SOURCE_TYPES,
  RULE_STATUS,
  SIGNAL_STATUS,
  SIGNAL_TYPES,
  SAVED_VIEW_ROOT_MODES,
  SAVED_VIEW_SHARE_SCOPES,
  SAVED_VIEW_STATUS,
  SKILL_STATUS,
  TARGET_LEVELS,
  TASK_STATUS,
  TRIGGER_EVENT_TYPES,
  TOOL_INVOCATION_STATUS,
  VIEW_LAYOUT_ENGINES,
  VIEW_SEMANTIC_LEVELS,
  UNKNOWN_TRANSITION_MODES,
  USER_STATUS,
} from '../constants/enums';

function isJsonText(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch (error) {
    if (error instanceof SyntaxError) return false;
    return false;
  }
}

const jsonTextSchema = z.string().min(1).refine(isJsonText, { message: 'must be valid JSON text' });

// --- Users -----------------------------------------------------------------

export const createUserSchema = z.object({
  email: z.string().email(),
  display_name: z.string().min(1),
  status: z.enum(USER_STATUS).optional(),
});
export const updateUserSchema = createUserSchema.partial();
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const createActorSchema = z.object({
  actor_type: z.enum(ACTOR_TYPES),
  display_name: z.string().min(1),
  status: z.enum(ACTOR_STATUS).optional(),
  default_user_id: z.string().nullable().optional(),
});
export const updateActorSchema = createActorSchema.partial();
export type CreateActorInput = z.infer<typeof createActorSchema>;
export type UpdateActorInput = z.infer<typeof updateActorSchema>;

export const createIdentitySchema = z.object({
  actor_id: z.string().min(1),
  provider: z.string().min(1),
  provider_subject: z.string().min(1),
  email: z.string().email().nullable().optional(),
  status: z.enum(IDENTITY_STATUS).optional(),
  last_seen_at: z.string().nullable().optional(),
});
export const updateIdentitySchema = createIdentitySchema.partial();
export type CreateIdentityInput = z.infer<typeof createIdentitySchema>;
export type UpdateIdentityInput = z.infer<typeof updateIdentitySchema>;

export const createActorMembershipSchema = z.object({
  member_actor_id: z.string().min(1),
  parent_actor_id: z.string().min(1),
  membership_type: z.enum(ACTOR_MEMBERSHIP_TYPES),
  context_id: z.string().nullable().optional(),
  starts_at: z.string().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  status: z.enum(AUTHORITY_STATUSES).optional(),
});
export const updateActorMembershipSchema = createActorMembershipSchema.partial();
export type CreateActorMembershipInput = z.infer<typeof createActorMembershipSchema>;
export type UpdateActorMembershipInput = z.infer<typeof updateActorMembershipSchema>;

export const createRoleAssignmentSchema = z.object({
  actor_id: z.string().min(1),
  role_id: z.string().min(1),
  context_id: z.string().nullable().optional(),
  resource_type: z.string().nullable().optional(),
  resource_id: z.string().nullable().optional(),
  source_type: z.enum(ROLE_ASSIGNMENT_SOURCE_TYPES).optional(),
  source_id: z.string().nullable().optional(),
  starts_at: z.string().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  status: z.enum(AUTHORITY_STATUSES).optional(),
});
export const updateRoleAssignmentSchema = createRoleAssignmentSchema.partial();
export type CreateRoleAssignmentInput = z.infer<typeof createRoleAssignmentSchema>;
export type UpdateRoleAssignmentInput = z.infer<typeof updateRoleAssignmentSchema>;

export const createDelegationGrantSchema = z.object({
  delegator_actor_id: z.string().min(1),
  delegate_actor_id: z.string().min(1),
  context_id: z.string().nullable().optional(),
  capability: z.string().min(1),
  resource_type: z.string().nullable().optional(),
  resource_id: z.string().nullable().optional(),
  ceiling_policy_id: z.string().nullable().optional(),
  starts_at: z.string().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  status: z.enum(AUTHORITY_STATUSES).optional(),
});
export const updateDelegationGrantSchema = createDelegationGrantSchema.partial();
export type CreateDelegationGrantInput = z.infer<typeof createDelegationGrantSchema>;
export type UpdateDelegationGrantInput = z.infer<typeof updateDelegationGrantSchema>;

export const createActionPolicySchema = z.object({
  context_id: z.string().nullable().optional(),
  capability: z.string().min(1),
  resource_type: z.string().nullable().optional(),
  risk_level: z.enum(ACTION_POLICY_RISK_LEVELS).optional(),
  approval_mode: z.enum(APPROVAL_MODES).optional(),
  required_approver_count: z.number().int().min(0).optional(),
  requires_distinct_approver: z.number().int().min(0).max(1).optional(),
  policy_rank: z.number().int().optional(),
  version: z.number().int().min(1).optional(),
  status: z.enum(AUTHORITY_STATUSES).optional(),
});
export const updateActionPolicySchema = createActionPolicySchema.partial();
export type CreateActionPolicyInput = z.infer<typeof createActionPolicySchema>;
export type UpdateActionPolicyInput = z.infer<typeof updateActionPolicySchema>;

export const createApprovalRequestSchema = z.object({
  source_type: z.string().min(1),
  source_id: z.string().min(1),
  capability: z.string().min(1),
  context_id: z.string().nullable().optional(),
  resource_type: z.string().nullable().optional(),
  resource_id: z.string().nullable().optional(),
  requested_by_actor_id: z.string().min(1),
  on_behalf_of_actor_id: z.string().nullable().optional(),
  authorized_via_type: z.string().nullable().optional(),
  authorized_via_id: z.string().nullable().optional(),
  authorized_via_membership_id: z.string().nullable().optional(),
  approval_mode: z.enum(APPROVAL_MODES),
  required_approver_count: z.number().int().min(0).optional(),
  requires_distinct_approver: z.number().int().min(0).max(1).optional(),
  rule_version: z.number().int().nullable().optional(),
  policy_version: z.number().int().nullable().optional(),
  status: z.enum(APPROVAL_REQUEST_STATUS).optional(),
  idempotency_key: z.string().min(1),
  payload_hash: z.string().min(1),
});
export const updateApprovalRequestSchema = createApprovalRequestSchema.partial();
export type CreateApprovalRequestInput = z.infer<typeof createApprovalRequestSchema>;
export type UpdateApprovalRequestInput = z.infer<typeof updateApprovalRequestSchema>;

export const createApprovalDecisionSchema = z.object({
  approval_request_id: z.string().min(1),
  deciding_actor_id: z.string().min(1),
  decision: z.enum(APPROVAL_DECISIONS),
  reason: z.string().nullable().optional(),
  decided_at: z.string().min(1),
  idempotency_key: z.string().min(1),
});
export type CreateApprovalDecisionInput = z.infer<typeof createApprovalDecisionSchema>;

const actionCardCreateObjectSchema = z.object({
    context_id: z.string().min(1),
    subject_type: z.enum(ACTION_CARD_SUBJECT_TYPES),
    subject_id: z.string().min(1).nullable().optional(),
    title: z.string().min(1),
    summary: z.string().nullable().optional(),
    card_type: z.enum(ACTION_CARD_TYPES),
    category: z.enum(ACTION_CARD_CATEGORIES),
    severity: z.enum(ACTION_CARD_SEVERITIES).default('medium'),
    risk_level: z.enum(ACTION_POLICY_RISK_LEVELS).default('low'),
    confidence: z.number().min(0).max(1).nullable().optional(),
    priority_score: z.number().min(0).nullable().optional(),
    reason: z.string().min(1),
    suggested_action_type: z.enum(ACTION_CARD_SUGGESTED_ACTION_TYPES),
    command_preview_json: jsonTextSchema,
    expected_effect_json: jsonTextSchema.nullable().optional(),
    approval_mode: z.enum(APPROVAL_MODES),
    action_policy_ref: z.string().nullable().optional(),
    status: z.enum(ACTION_CARD_STATUSES).default('proposed'),
    defer_until: z.string().nullable().optional(),
    defer_reason: z.string().nullable().optional(),
    reject_reason: z.string().nullable().optional(),
    suppression_until: z.string().nullable().optional(),
    resolved_at: z.string().nullable().optional(),
    generated_by_type: z.enum(ACTION_CARD_GENERATED_BY_TYPES),
    generated_by_id: z.string().min(1),
    source_event_id: z.string().nullable().optional(),
    rule_run_id: z.string().nullable().optional(),
    agent_run_id: z.string().nullable().optional(),
    approval_request_id: z.string().nullable().optional(),
    decision_id: z.string().nullable().optional(),
    execution_ref_type: z.string().nullable().optional(),
    execution_ref_id: z.string().nullable().optional(),
    execution_status: z.string().nullable().optional(),
    execution_error: z.string().nullable().optional(),
    dedupe_key: z.string().min(1),
    occurrence_count: z.number().int().min(1).default(1),
    first_seen_at: z.string().min(1),
    last_seen_at: z.string().min(1),
    expires_at: z.string().nullable().optional(),
    assigned_actor_id: z.string().nullable().optional(),
    audience_role: z.string().nullable().optional(),
    accepted_by_actor_id: z.string().nullable().optional(),
    rejected_by_actor_id: z.string().nullable().optional(),
});

export const createActionCardSchema = actionCardCreateObjectSchema.superRefine((card, ctx) => {
    if (card.status === 'deferred' && !card.defer_until) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'deferred cards require defer_until',
        path: ['defer_until'],
      });
    }
    if (card.status !== 'rejected' && card.reject_reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'reject_reason belongs only to rejected cards',
        path: ['reject_reason'],
      });
    }
});
export const updateActionCardSchema = actionCardCreateObjectSchema.partial();
export type CreateActionCardInput = z.infer<typeof createActionCardSchema>;
export type UpdateActionCardInput = z.infer<typeof updateActionCardSchema>;

export const createActionCardLinkSchema = z.object({
  action_card_id: z.string().min(1),
  entity_type: z.enum(ACTION_CARD_SUBJECT_TYPES),
  entity_id: z.string().min(1),
  link_type: z.enum(ACTION_CARD_LINK_TYPES),
  visibility: z.enum(ACTION_CARD_LINK_VISIBILITY).default('normal'),
});
export const updateActionCardLinkSchema = createActionCardLinkSchema.partial();
export type CreateActionCardLinkInput = z.infer<typeof createActionCardLinkSchema>;
export type UpdateActionCardLinkInput = z.infer<typeof updateActionCardLinkSchema>;

export const createCredentialSchema = z.object({
  provider: z.string().min(1),
  tool_name: z.string().min(1),
  display_name: z.string().min(1),
  secret_ref: z.string().min(1),
  status: z.enum(CREDENTIAL_STATUS).optional(),
  created_by_actor_id: z.string().nullable().optional(),
  rotated_at: z.string().nullable().optional(),
});
export const updateCredentialSchema = createCredentialSchema.partial();
export type CreateCredentialInput = z.infer<typeof createCredentialSchema>;
export type UpdateCredentialInput = z.infer<typeof updateCredentialSchema>;

export const createCredentialGrantSchema = z.object({
  credential_id: z.string().min(1),
  connector_id: z.string().nullable().optional(),
  actor_id: z.string().nullable().optional(),
  context_id: z.string().nullable().optional(),
  package_id: z.string().nullable().optional(),
  resource_type: z.string().nullable().optional(),
  resource_id: z.string().nullable().optional(),
  tool_name: z.string().min(1),
  capability: z.string().min(1),
  allowed_scopes_json: z.string().optional(),
  scope_hash: z.string().min(1),
  status: z.enum(CREDENTIAL_GRANT_STATUS).optional(),
  starts_at: z.string().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  created_by_actor_id: z.string().nullable().optional(),
});
export const updateCredentialGrantSchema = createCredentialGrantSchema.partial();
export type CreateCredentialGrantInput = z.infer<typeof createCredentialGrantSchema>;
export type UpdateCredentialGrantInput = z.infer<typeof updateCredentialGrantSchema>;

// --- Companies -------------------------------------------------------------

export const createCompanySchema = z.object({
  name: z.string().min(1),
  status: z.enum(ENTITY_STATUS).optional(),
});
export const updateCompanySchema = createCompanySchema.partial();
export type CreateCompanyInput = z.infer<typeof createCompanySchema>;
export type UpdateCompanyInput = z.infer<typeof updateCompanySchema>;

// --- Projects --------------------------------------------------------------

export const createProjectSchema = z.object({
  company_id: z.string().nullable().optional(),
  name: z.string().min(1),
  status: z.enum(ENTITY_STATUS).optional(),
  description: z.string().nullable().optional(),
});
export const updateProjectSchema = createProjectSchema.partial();
export type CreateProjectInput = z.infer<typeof createProjectSchema>;
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

// --- Artifacts -------------------------------------------------------------

export const createArtifactSchema = z.object({
  project_id: z.string().nullable().optional(),
  artifact_type: z.enum(ARTIFACT_TYPES),
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  status: z.enum(ARTIFACT_STATUS).optional(),
  owner_user_id: z.string().nullable().optional(),
  priority: z.enum(PRIORITY).nullable().optional(),
  due_date: z.string().nullable().optional(),
  source_system: z.string().nullable().optional(),
  external_url: z.string().nullable().optional(),
  // --- v0.2 (CONTRACT_V2 §4.4) ---
  primary_context_id: z.string().nullable().optional(),
  crystallization_stage: z.enum(CRYSTALLIZATION_STAGES).optional(),
  confidentiality: z.enum(CONFIDENTIALITY).optional(),
});
export const updateArtifactSchema = createArtifactSchema.partial();
export type CreateArtifactInput = z.infer<typeof createArtifactSchema>;
export type UpdateArtifactInput = z.infer<typeof updateArtifactSchema>;

// --- Tasks -----------------------------------------------------------------

export const createTaskSchema = z.object({
  artifact_id: z.string().min(1),
  instruction: z.string().nullable().optional(),
  result: z.string().nullable().optional(),
  assignee_user_id: z.string().nullable().optional(),
  task_status: z.enum(TASK_STATUS).optional(),
});
export const updateTaskSchema = createTaskSchema.partial();
export type CreateTaskInput = z.infer<typeof createTaskSchema>;
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;

// --- Documents -------------------------------------------------------------

export const createDocumentSchema = z.object({
  project_id: z.string().nullable().optional(),
  title: z.string().min(1),
  source_system: z.string().min(1),
  external_url: z.string().nullable().optional(),
  document_type: z.string().nullable().optional(),
  confidentiality: z.enum(CONFIDENTIALITY).optional(),
  canonical_status: z.string().optional(),
  owner_user_id: z.string().nullable().optional(),
  agent_summary: z.string().nullable().optional(),
  last_reviewed_at: z.string().nullable().optional(),
});
export const updateDocumentSchema = createDocumentSchema.partial();
export type CreateDocumentInput = z.infer<typeof createDocumentSchema>;
export type UpdateDocumentInput = z.infer<typeof updateDocumentSchema>;

// --- Evidence --------------------------------------------------------------

export const createEvidenceSchema = z.object({
  artifact_id: z.string().nullable().optional(),
  evidence_type: z.string().min(1),
  title: z.string().min(1),
  body: z.string().nullable().optional(),
  confidence_score: z.number().nullable().optional(),
  weight: z.number().nullable().optional(),
  source_event_id: z.string().nullable().optional(),
  source_document_id: z.string().nullable().optional(),
  created_by_user_id: z.string().nullable().optional(),
  // --- v0.2 (CONTRACT_V2 §4.4 / §9) ---
  claim_type: z.enum(CLAIM_TYPES).nullable().optional(),
  claim_id: z.string().nullable().optional(),
  polarity: z.enum(EVIDENCE_POLARITY).optional(),
  source_signal_id: z.string().nullable().optional(),
  source_artifact_id: z.string().nullable().optional(),
  interpretation: z.string().nullable().optional(),
  idempotency_key: z.string().nullable().optional(),
});
export const updateEvidenceSchema = createEvidenceSchema.partial();
export type CreateEvidenceInput = z.infer<typeof createEvidenceSchema>;
export type UpdateEvidenceInput = z.infer<typeof updateEvidenceSchema>;

// --- Objectives ------------------------------------------------------------

export const createObjectiveSchema = z.object({
  project_id: z.string().nullable().optional(),
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  owner_user_id: z.string().nullable().optional(),
  period: z.string().nullable().optional(),
  status: z.enum(ENTITY_STATUS).optional(),
});
export const updateObjectiveSchema = createObjectiveSchema.partial();
export type CreateObjectiveInput = z.infer<typeof createObjectiveSchema>;
export type UpdateObjectiveInput = z.infer<typeof updateObjectiveSchema>;

// --- Key Results -----------------------------------------------------------

export const createKeyResultSchema = z.object({
  objective_id: z.string().min(1),
  title: z.string().min(1),
  target_value: z.number().nullable().optional(),
  current_value: z.number().nullable().optional(),
  unit: z.string().nullable().optional(),
  metric_id: z.string().nullable().optional(),
  status: z.enum(ENTITY_STATUS).optional(),
});
export const updateKeyResultSchema = createKeyResultSchema.partial();
export type CreateKeyResultInput = z.infer<typeof createKeyResultSchema>;
export type UpdateKeyResultInput = z.infer<typeof updateKeyResultSchema>;

// --- Metrics ---------------------------------------------------------------

export const createMetricSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  unit: z.string().nullable().optional(),
  owner_user_id: z.string().nullable().optional(),
});
export const updateMetricSchema = createMetricSchema.partial();
export type CreateMetricInput = z.infer<typeof createMetricSchema>;
export type UpdateMetricInput = z.infer<typeof updateMetricSchema>;

// --- Measurements ----------------------------------------------------------

export const createMeasurementSchema = z.object({
  metric_id: z.string().min(1),
  measured_at: z.string().min(1),
  value: z.number(),
  source_system: z.string().nullable().optional(),
  source_artifact_id: z.string().nullable().optional(),
});
export const updateMeasurementSchema = createMeasurementSchema.partial();
export type CreateMeasurementInput = z.infer<typeof createMeasurementSchema>;
export type UpdateMeasurementInput = z.infer<typeof updateMeasurementSchema>;

// --- Skills ----------------------------------------------------------------

export const createSkillSchema = z.object({
  name: z.string().min(1),
  domain: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  status: z.enum(SKILL_STATUS).optional(),
  owner_user_id: z.string().nullable().optional(),
  short_summary: z.string().nullable().optional(),
  body_markdown: z.string().nullable().optional(),
  input_contract: z.string().nullable().optional(),
  output_contract: z.string().nullable().optional(),
  allowed_executor_type: z.enum(EXECUTOR_TYPES).nullable().optional(),
  version: z.number().int().optional(),
});
export const updateSkillSchema = createSkillSchema.partial();
export type CreateSkillInput = z.infer<typeof createSkillSchema>;
export type UpdateSkillInput = z.infer<typeof updateSkillSchema>;

// --- Processes -------------------------------------------------------------

export const createProcessSchema = z.object({
  name: z.string().min(1),
  domain: z.string().nullable().optional(),
  artifact_type: z.enum(ARTIFACT_TYPES).nullable().optional(),
  rigidity_level: z.number().int().optional(),
  owner_user_id: z.string().nullable().optional(),
  purpose: z.string().nullable().optional(),
  status: z.enum(ENTITY_STATUS).optional(),
});
export const updateProcessSchema = createProcessSchema.partial();
export type CreateProcessInput = z.infer<typeof createProcessSchema>;
export type UpdateProcessInput = z.infer<typeof updateProcessSchema>;

// --- Phases ----------------------------------------------------------------

export const createPhaseSchema = z.object({
  process_id: z.string().min(1),
  name: z.string().min(1),
  sort_order: z.number().int().nullable().optional(),
  purpose: z.string().nullable().optional(),
  context_markdown: z.string().nullable().optional(),
  is_start: z.number().int().min(0).max(1).optional(),
  is_terminal: z.number().int().min(0).max(1).optional(),
});
export const updatePhaseSchema = createPhaseSchema.partial();
export type CreatePhaseInput = z.infer<typeof createPhaseSchema>;
export type UpdatePhaseInput = z.infer<typeof updatePhaseSchema>;

// --- Rules -----------------------------------------------------------------

export const createRuleSchema = z.object({
  name: z.string().min(1),
  status: z.enum(RULE_STATUS).optional(),
  trigger_event_type: z.enum(TRIGGER_EVENT_TYPES),
  artifact_type: z.enum(ARTIFACT_TYPES).nullable().optional(),
  required_status: z.string().nullable().optional(),
  required_phase_id: z.string().nullable().optional(),
  required_evidence_type: z.string().nullable().optional(),
  required_evidence_count: z.number().int().nullable().optional(),
  effect_type: z.enum(EFFECT_TYPES),
  target_status: z.string().nullable().optional(),
  target_phase_id: z.string().nullable().optional(),
  target_skill_id: z.string().nullable().optional(),
  executor_type: z.enum(EXECUTOR_TYPES).nullable().optional(),
  emitted_event_type: z.enum(EVENT_TYPES).nullable().optional(),
  priority: z.number().int().optional(),
  version: z.number().int().optional(),
  owner_user_id: z.string().nullable().optional(),
  source_path: z.string().nullable().optional(),
  // --- v0.2 (CONTRACT_V2 §4.4 / §5.7) — advisory; never blocks rule execution ---
  required_context: z.string().nullable().optional(), // JSON array of context refs
  context_policy: z.enum(CONTEXT_POLICIES).optional(),
  // --- contour-chains (CONTOUR-CHAINS-CONTRACT.md §4-a) — skill-hop discriminator ---
  required_skill_id: z.string().nullable().optional(),
});
export const updateRuleSchema = createRuleSchema.partial();
export type CreateRuleInput = z.infer<typeof createRuleSchema>;
export type UpdateRuleInput = z.infer<typeof updateRuleSchema>;

// --- Decisions -------------------------------------------------------------

export const createDecisionSchema = z.object({
  artifact_id: z.string().nullable().optional(),
  project_id: z.string().nullable().optional(),
  title: z.string().min(1),
  description: z.string().nullable().optional(),
  decision_type: z.string().nullable().optional(),
  status: z.enum(DECISION_STATUS).optional(),
  rationale: z.string().nullable().optional(),
  decided_by_user_id: z.string().nullable().optional(),
  requested_by_user_id: z.string().nullable().optional(),
  confidentiality: z.enum(CONFIDENTIALITY).optional(),
  decided_at: z.string().nullable().optional(),
  idempotency_key: z.string().nullable().optional(),
});
export const updateDecisionSchema = createDecisionSchema.partial();
export type CreateDecisionInput = z.infer<typeof createDecisionSchema>;
export type UpdateDecisionInput = z.infer<typeof updateDecisionSchema>;

// ===========================================================================
// v0.2 — new CRUD resources (CONTRACT_V2 §4.4). Defaulted columns are
// `.optional()`; the repo/route fills the DB default. id/created_at/updated_at
// are server-managed and excluded.
// ===========================================================================

// --- Contexts --------------------------------------------------------------

export const createContextSchema = z.object({
  name: z.string().min(1),
  context_type: z.enum(CONTEXT_TYPES),
  description: z.string().nullable().optional(),
  host_type: z.string().nullable().optional(),
  host_id: z.string().nullable().optional(),
  boundary: z.string().nullable().optional(),
  maturity_stage: z.enum(CONTEXT_MATURITY_STAGES).optional(),
  priority: z.number().int().optional(),
  status: z.string().optional(),
  owner_user_id: z.string().nullable().optional(),
  confidentiality: z.enum(CONFIDENTIALITY).optional(),
  review_at: z.string().nullable().optional(),
  origin_context_id: z.string().nullable().optional(),
  parent_context_id: z.string().nullable().optional(),
  monitoring_profile_id: z.string().nullable().optional(),
  last_advanced_at: z.string().nullable().optional(),
  crystallization_target_type: z.enum(CONTEXT_CRYSTALLIZATION_TARGET_TYPES).nullable().optional(),
  watch_config: z.string().nullable().optional(),
  signal_velocity: z.number().nullable().optional(),
  dwell_days_in_band: z.number().int().nullable().optional(),
  crystallization_readiness: z.number().nullable().optional(),
  trend_indicator: z.string().nullable().optional(),
});
export const updateContextSchema = createContextSchema.partial();
export type CreateContextInput = z.infer<typeof createContextSchema>;
export type UpdateContextInput = z.infer<typeof updateContextSchema>;

export const createContextMonitoringProfileSchema = z.object({
  context_id: z.string().min(1),
  profile_kind: z.enum(CONTEXT_MONITORING_PROFILE_KINDS).optional(),
  cadence: z.enum(CONTEXT_MONITORING_CADENCES),
  saved_view_id: z.string().nullable().optional(),
  default_lens_id: z.string().nullable().optional(),
  review_cadence: z.enum(CONTEXT_MONITORING_CADENCES).nullable().optional(),
  next_review_at: z.string().nullable().optional(),
  action_mode: z.enum(CONTEXT_ACTION_MODES).optional(),
  watched_signal_types_json: jsonTextSchema.optional(),
  watch_query_json: jsonTextSchema.optional(),
  diagnostic_pack_id: z.string().nullable().optional(),
  assistant_agent_id: z.string().nullable().optional(),
  owner_actor_id: z.string().nullable().optional(),
  last_reviewed_at: z.string().nullable().optional(),
  created_by_actor_id: z.string().nullable().optional(),
  signal_filters_json: z.string().optional(),
  thresholds_json: z.string().optional(),
  diagnostics_json: z.string().optional(),
  status: z.enum(CONTEXT_MONITORING_STATUS).optional(),
});
export const updateContextMonitoringProfileSchema = createContextMonitoringProfileSchema.partial();
export type CreateContextMonitoringProfileInput = z.infer<
  typeof createContextMonitoringProfileSchema
>;
export type UpdateContextMonitoringProfileInput = z.infer<
  typeof updateContextMonitoringProfileSchema
>;

export const createLensSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  version: z.string().min(1),
  maturity: z.enum(LENS_MATURITY).optional(),
  validation_status: z.enum(LENS_VALIDATION_STATUS).optional(),
  spec_hash: z.string().nullable().optional(),
  root_types_json: z.string().min(1),
  dsl_json: z.string().min(1),
  default_layout_engine: z.enum(VIEW_LAYOUT_ENGINES).optional(),
  owner_user_id: z.string().nullable().optional(),
});
export const updateLensSchema = createLensSchema.partial();
export type CreateLensInput = z.infer<typeof createLensSchema>;
export type UpdateLensInput = z.infer<typeof updateLensSchema>;

export const createSavedViewSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  lens_id: z.string().min(1),
  lens_version: z.string().min(1),
  root_mode: z.enum(SAVED_VIEW_ROOT_MODES),
  root_context_id: z.string().nullable().optional(),
  root_selector_json: z.string().nullable().optional(),
  params_json: z.string().optional(),
  layout_overrides_json: z.string().optional(),
  semantic_level: z.enum(VIEW_SEMANTIC_LEVELS).optional(),
  share_scope: z.enum(SAVED_VIEW_SHARE_SCOPES).optional(),
  pinned: z.number().int().min(0).max(1).optional(),
  default_for_context_id: z.string().nullable().optional(),
  default_for_context_type: z.enum(CONTEXT_TYPES).nullable().optional(),
  status: z.enum(SAVED_VIEW_STATUS).optional(),
  owner_user_id: z.string().nullable().optional(),
});
export const updateSavedViewSchema = createSavedViewSchema.partial();
export type CreateSavedViewInput = z.infer<typeof createSavedViewSchema>;
export type UpdateSavedViewInput = z.infer<typeof updateSavedViewSchema>;

// --- Context bindings ------------------------------------------------------

export const createContextBindingSchema = z.object({
  context_id: z.string().min(1),
  entity_type: z.enum(BINDING_ENTITY_TYPES),
  entity_id: z.string().min(1),
  binding_type: z.enum(BINDING_TYPES),
  priority: z.number().int().optional(),
  access_mode: z.enum(ACCESS_MODES).optional(),
  active_from: z.string().nullable().optional(),
  active_to: z.string().nullable().optional(),
  deployment_id: z.string().nullable().optional(),
  deployment_status: z.enum(PACKAGE_DEPLOYMENT_STATUSES).nullable().optional(),
  pinned_package_version_id: z.string().nullable().optional(),
  pinned_content_hash: z.string().nullable().optional(),
  config_json: z.string().nullable().optional(),
  config_fingerprint: z.string().nullable().optional(),
  resolved_package_lock_json: z.string().nullable().optional(),
  resolved_package_lock_hash: z.string().nullable().optional(),
  upgrade_mode: z.enum(PACKAGE_DEPLOYMENT_UPGRADE_MODES).nullable().optional(),
  approval_policy_ref: z.string().nullable().optional(),
  deployed_by_actor_id: z.string().nullable().optional(),
  approved_by_actor_id: z.string().nullable().optional(),
  authorized_via_type: z.string().nullable().optional(),
  authorized_via_id: z.string().nullable().optional(),
  authorized_via_membership_id: z.string().nullable().optional(),
  source_action_card_id: z.string().nullable().optional(),
  operating_context_id: z.string().nullable().optional(),
  idempotency_key: z.string().nullable().optional(),
});
export const updateContextBindingSchema = createContextBindingSchema.partial();
export type CreateContextBindingInput = z.infer<typeof createContextBindingSchema>;
export type UpdateContextBindingInput = z.infer<typeof updateContextBindingSchema>;

// --- Signals ---------------------------------------------------------------

export const createSignalSchema = z.object({
  context_id: z.string().min(1),
  signal_type: z.enum(SIGNAL_TYPES),
  title: z.string().min(1),
  body: z.string().nullable().optional(),
  source_type: z.string().nullable().optional(),
  source_id: z.string().nullable().optional(),
  strength: z.number().nullable().optional(),
  confidence_score: z.number().nullable().optional(),
  status: z.enum(SIGNAL_STATUS).optional(),
  candidate_package_type: z.enum(PACKAGE_TYPES).nullable().optional(),
  candidate_package_id: z.string().nullable().optional(),
  created_by_user_id: z.string().nullable().optional(),
});
export const updateSignalSchema = createSignalSchema.partial();
export type CreateSignalInput = z.infer<typeof createSignalSchema>;
export type UpdateSignalInput = z.infer<typeof updateSignalSchema>;

// --- Packages --------------------------------------------------------------

export const createPackageSchema = z.object({
  package_type: z.enum(PACKAGE_TYPES),
  target_level: z.enum(TARGET_LEVELS),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  domain: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  status: z.string().optional(),
  maturity_stage: z.enum(PACKAGE_MATURITY_STAGES).optional(),
  owner_user_id: z.string().nullable().optional(),
  body_markdown: z.string().nullable().optional(),
  source_path: z.string().nullable().optional(),
  version: z.number().int().optional(),
  short_summary: z.string().nullable().optional(),
  input_contract: z.string().nullable().optional(),
  output_contract: z.string().nullable().optional(),
  allowed_executor_type: z.enum(EXECUTOR_TYPES).nullable().optional(),
  object_type: z.enum(ARTIFACT_TYPES).nullable().optional(),
  rigidity_level: z.number().int().optional(),
  auto_transition: z.number().int().min(0).max(1).optional(),
  unknown_transition: z.enum(UNKNOWN_TRANSITION_MODES).optional(),
  modes: z.string().nullable().optional(), // JSON array of weak-contour modes
});
export const updatePackageSchema = createPackageSchema.partial();
export type CreatePackageInput = z.infer<typeof createPackageSchema>;
export type UpdatePackageInput = z.infer<typeof updatePackageSchema>;

// --- Package links (append-only: no update schema) -------------------------

export const createPackageLinkSchema = z.object({
  source_package_id: z.string().min(1),
  target_package_id: z.string().min(1),
  link_type: z.enum(LINK_TYPES),
  description: z.string().nullable().optional(),
});
export type CreatePackageLinkInput = z.infer<typeof createPackageLinkSchema>;

export const createPackageVersionSchema = z.object({
  package_id: z.string().min(1),
  version: z.number().int().positive(),
  status: z.enum(PACKAGE_VERSION_STATUSES).optional(),
  content_hash: z.string().min(1),
  r2_main_key: z.string().nullable().optional(),
  diff_ref: z.string().nullable().optional(),
  diff_summary: z.string().nullable().optional(),
  rationale: z.string().nullable().optional(),
  proposed_by_actor_id: z.string().nullable().optional(),
  source_rollup_id: z.string().nullable().optional(),
  source_action_card_id: z.string().nullable().optional(),
  source_artifact_id: z.string().nullable().optional(),
  risk_level: z.enum(PACKAGE_IMPROVEMENT_RISK_LEVELS).optional(),
  reviewed_by_actor_id: z.string().nullable().optional(),
  decided_at: z.string().nullable().optional(),
});
export const updatePackageVersionSchema = createPackageVersionSchema.partial();
export type CreatePackageVersionInput = z.infer<typeof createPackageVersionSchema>;
export type UpdatePackageVersionInput = z.infer<typeof updatePackageVersionSchema>;

export const createPackageUsageRollupSchema = z.object({
  package_id: z.string().min(1),
  package_version_id: z.string().nullable().optional(),
  deployment_id: z.string().nullable().optional(),
  deployment_context_id: z.string().nullable().optional(),
  window_start: z.string().min(1),
  window_end: z.string().min(1),
  invocations: z.number().int().min(0),
  successes: z.number().int().min(0),
  failures: z.number().int().min(0),
  validation_failures: z.number().int().min(0),
  human_review_count: z.number().int().min(0).optional(),
  policy_denial_count: z.number().int().min(0).optional(),
  retry_count: z.number().int().min(0).optional(),
  avg_latency_ms: z.number().min(0).nullable().optional(),
  p95_latency_ms: z.number().min(0).nullable().optional(),
  estimated_cost_cents: z.number().int().min(0).nullable().optional(),
  failure_signatures_json: z.string().optional(),
  sample_size: z.number().int().min(0),
  sample_threshold_met: z.number().int().min(0).max(1).optional(),
  privacy_visibility: z.enum(PACKAGE_USAGE_PRIVACY_VISIBILITY).optional(),
  redaction_summary: z.string().optional(),
  computed_at: z.string().min(1),
});
export const updatePackageUsageRollupSchema = createPackageUsageRollupSchema.partial();
export type CreatePackageUsageRollupInput = z.infer<
  typeof createPackageUsageRollupSchema
>;
export type UpdatePackageUsageRollupInput = z.infer<
  typeof updatePackageUsageRollupSchema
>;

// --- Claims ----------------------------------------------------------------

export const createClaimSchema = z.object({
  claim_type: z.enum(CLAIM_TYPES),
  package_id: z.string().nullable().optional(),
  artifact_id: z.string().nullable().optional(),
  title: z.string().min(1),
  statement: z.string().min(1),
  status: z.enum(CLAIM_STATUS).optional(),
  confidence_score: z.number().nullable().optional(),
  idempotency_key: z.string().nullable().optional(),
});
export const updateClaimSchema = createClaimSchema.partial();
export type CreateClaimInput = z.infer<typeof createClaimSchema>;
export type UpdateClaimInput = z.infer<typeof updateClaimSchema>;

// --- Artifact positions ----------------------------------------------------

export const createArtifactPositionSchema = z.object({
  artifact_id: z.string().min(1),
  process_id: z.string().min(1), // contour package id (D2)
  phase_id: z.string().min(1),
  contour_instance_id: z.string().nullable().optional(),
  membership_score: z.number().optional(),
  confidence_score: z.number().nullable().optional(),
  is_primary: z.number().int().min(0).max(1).optional(),
  entered_at: z.string().nullable().optional(),
  last_event_id: z.string().nullable().optional(),
});
export const updateArtifactPositionSchema = createArtifactPositionSchema.partial();
export type CreateArtifactPositionInput = z.infer<typeof createArtifactPositionSchema>;
export type UpdateArtifactPositionInput = z.infer<typeof updateArtifactPositionSchema>;

// --- Timers ----------------------------------------------------------------

export const createTimerSchema = z.object({
  name: z.string().min(1),
  schedule: z.string().min(1), // cron-ish "m h dom mon dow"
  emitted_event_type: z.enum(EVENT_TYPES),
  status: z.string().optional(),
  context_id: z.string().nullable().optional(),
  last_fired_at: z.string().nullable().optional(),
  next_due_at: z.string().nullable().optional(),
});
export const updateTimerSchema = createTimerSchema.partial();
export type CreateTimerInput = z.infer<typeof createTimerSchema>;
export type UpdateTimerInput = z.infer<typeof updateTimerSchema>;

export const createAgentProfileSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  kind: z.enum(AGENT_PROFILE_KINDS),
  specialization: z.enum(AGENT_PROFILE_SPECIALIZATIONS).nullable().optional(),
  owner_actor_id: z.string().nullable().optional(),
  home_context_id: z.string().nullable().optional(),
  status: z.enum(AGENT_PROFILE_STATUS).optional(),
  current_version_id: z.string().nullable().optional(),
});
export const updateAgentProfileSchema = createAgentProfileSchema.partial();
export type CreateAgentProfileInput = z.infer<typeof createAgentProfileSchema>;
export type UpdateAgentProfileInput = z.infer<typeof updateAgentProfileSchema>;

export const createAgentProfileVersionSchema = z.object({
  profile_id: z.string().min(1),
  version: z.number().int().positive(),
  status: z.enum(AGENT_PROFILE_VERSION_STATUS).optional(),
  instructions_artifact_id: z.string().nullable().optional(),
  instructions_markdown: z.string().min(1),
  role_instruction_policy_json: jsonTextSchema.optional(),
  model_policy_json: jsonTextSchema.optional(),
  context_policy_json: jsonTextSchema.optional(),
  memory_policy_json: jsonTextSchema.optional(),
  autonomy_policy_json: jsonTextSchema.optional(),
  trigger_policy_json: jsonTextSchema.optional(),
  skills_json: jsonTextSchema.optional(),
  tools_json: jsonTextSchema.optional(),
  output_schema_json: jsonTextSchema,
  harness_json: jsonTextSchema.optional(),
  run_policy_json: jsonTextSchema,
  created_by_actor_id: z.string().nullable().optional(),
  promoted_at: z.string().nullable().optional(),
  retired_at: z.string().nullable().optional(),
});
export const updateAgentProfileVersionSchema = createAgentProfileVersionSchema.partial();
export type CreateAgentProfileVersionInput = z.infer<typeof createAgentProfileVersionSchema>;
export type UpdateAgentProfileVersionInput = z.infer<typeof updateAgentProfileVersionSchema>;

export const agentBindingActivationEntrySchema = z.object({
  kind: z.enum(AGENT_ACTIVATION_KINDS),
  source: z.enum(AGENT_ACTIVATION_SOURCES),
});

const agentBindingInputSchema = z.object({
  profile_id: z.string().min(1),
  profile_version_id: z.string().min(1),
  actor_id: z.string().min(1),
  context_id: z.string().min(1),
  role_assignment_id: z.string().nullable().optional(),
  activation_json: jsonTextSchema,
  trigger_ref_json: jsonTextSchema.nullable().optional(),
  schedule_ref_json: jsonTextSchema.nullable().optional(),
  manual_run_enabled: z.number().int().min(0).max(1).optional(),
  status: z.enum(AGENT_BINDING_STATUS).optional(),
  valid_from: z.string().nullable().optional(),
  valid_to: z.string().nullable().optional(),
});

function validateAgentBindingActivation(
  value: { readonly activation_json?: string },
  ctx: z.RefinementCtx,
): void {
  if (!value.activation_json) return;
  try {
    const parsed: unknown = JSON.parse(value.activation_json);
    const result = z.array(agentBindingActivationEntrySchema).min(1).safeParse(parsed);
    if (!result.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['activation_json'],
        message: 'activation_json must contain at least one supported activation entry',
      });
    }
  } catch (error) {
    if (error instanceof SyntaxError) return;
    throw error;
  }
}

export const createAgentBindingSchema = agentBindingInputSchema.superRefine(validateAgentBindingActivation);
export const updateAgentBindingSchema = agentBindingInputSchema.partial().superRefine(validateAgentBindingActivation);
export type CreateAgentBindingInput = z.infer<typeof createAgentBindingSchema>;
export type UpdateAgentBindingInput = z.infer<typeof updateAgentBindingSchema>;

export const createAgentHarnessRunSchema = z.object({
  profile_id: z.string().min(1),
  profile_version_id: z.string().min(1),
  harness_name: z.string().min(1),
  status: z.enum(AGENT_HARNESS_RUN_STATUS).optional(),
  fixture_context_snapshot_url: z.string().nullable().optional(),
  output_url: z.string().nullable().optional(),
  failure_summary: z.string().nullable().optional(),
  created_by_actor_id: z.string().nullable().optional(),
  started_at: z.string().min(1),
  finished_at: z.string().nullable().optional(),
});
export const updateAgentHarnessRunSchema = createAgentHarnessRunSchema.partial();
export type CreateAgentHarnessRunInput = z.infer<typeof createAgentHarnessRunSchema>;
export type UpdateAgentHarnessRunInput = z.infer<typeof updateAgentHarnessRunSchema>;

export const createToolInvocationSchema = z.object({
  agent_run_id: z.string().min(1),
  actor_id: z.string().min(1),
  on_behalf_of_actor_id: z.string().nullable().optional(),
  authorized_via_type: z.string().nullable().optional(),
  authorized_via_id: z.string().nullable().optional(),
  context_id: z.string().nullable().optional(),
  tool_name: z.string().min(1),
  capability: z.string().nullable().optional(),
  requested_action: z.string().min(1),
  approval_mode: z.enum(APPROVAL_MODES),
  approval_request_id: z.string().nullable().optional(),
  credential_grant_id: z.string().nullable().optional(),
  subject_type: z.string().nullable().optional(),
  subject_id: z.string().nullable().optional(),
  request_hash: z.string().nullable().optional(),
  idempotency_key: z.string().nullable().optional(),
  sanitized_request_json: jsonTextSchema.nullable().optional(),
  sanitized_result_json: jsonTextSchema.nullable().optional(),
  status: z.enum(TOOL_INVOCATION_STATUS).optional(),
  sanitized_input_url: z.string().nullable().optional(),
  sanitized_output_url: z.string().nullable().optional(),
  error_class: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  correlation_id: z.string().nullable().optional(),
  causation_id: z.string().nullable().optional(),
  started_at: z.string().nullable().optional(),
  finished_at: z.string().nullable().optional(),
});
export const updateToolInvocationSchema = createToolInvocationSchema.partial();
export type CreateToolInvocationInput = z.infer<typeof createToolInvocationSchema>;
export type UpdateToolInvocationInput = z.infer<typeof updateToolInvocationSchema>;
